import { useState } from "react";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { DataTable } from "./DataTable";
import { CrudDialog } from "./CrudDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { ExportButtons } from "./ExportButtons";
import { PageHeader } from "./PageHeader";

export function CrudPage({ title, subtitle, collection, basePath, columns, fields, searchKeys, canWrite = true, extraActions, extraHeader, importable, mapRows, refreshKey, groupBy, aboveTable, canDelete = () => true, rowClassName }) {
  const path = basePath || `/master/${collection}`;
  const { data, loading, refetch } = useFetch(path, [refreshKey]);
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const rows = mapRows ? mapRows(data || []) : (data || []);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const doDelete = async () => {
    try { await api.delete(`${path}/${del.id}`); toast.success("Data dihapus"); setSelected((s) => { const n = new Set(s); n.delete(del.id); return n; }); refetch(); } catch (e) { toast.error(errMsg(e)); } finally { setDel(null); }
  };
  const doBulkDelete = async () => {
    const ids = [...selected];
    const results = await Promise.allSettled(ids.map((id) => api.delete(`${path}/${id}`)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) toast.success(`${ok} data dihapus`);
    if (fail) toast.error(`${fail} data gagal dihapus: ${errMsg(results.find((r) => r.status === "rejected")?.reason)}`);
    setSelected(new Set()); setBulk(false); refetch();
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data: r } = await api.post(`/import/${collection}`, fd);
      toast.success(`Import selesai: ${r.inserted} ditambah, ${r.skipped} duplikat dilewati${r.errors.length ? `, ${r.errors.length} error` : ""}`);
      refetch();
    } catch (er) { toast.error(errMsg(er)); } finally { e.target.value = ""; }
  };

  return (
    <div className="space-y-4 fade-up">
      <PageHeader title={title} subtitle={subtitle}>
        {extraHeader}
        <ExportButtons collection={collection} />
        {canWrite && importable && (
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-accent" data-testid={`import-${collection}-btn`}>
            <Upload className="h-4 w-4" /> <span className="hidden sm:inline">Import Excel</span>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={doImport} />
          </label>
        )}
        {canWrite && selected.size > 0 && <Button size="sm" variant="destructive" onClick={() => setBulk(true)} data-testid={`bulk-delete-${collection}-btn`}><Trash2 className="h-4 w-4 mr-1" /> Hapus terpilih ({selected.size})</Button>}
        {canWrite && <Button size="sm" onClick={() => setDlg({ open: true, item: null })} data-testid={`add-${collection}-btn`}><Plus className="h-4 w-4 mr-1" /> Tambah</Button>}
      </PageHeader>
      {aboveTable?.(data || [])}
      <DataTable columns={columns} rows={rows} loading={loading} searchKeys={searchKeys} testId={`${collection}-table`} groupBy={groupBy} rowClassName={rowClassName}
        selected={canWrite ? selected : undefined} onToggle={toggle} onToggleAll={toggleAll} selectable={canDelete}
        actions={(r) => (
          <>
            {extraActions?.(r, refetch)}
            {canWrite && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDlg({ open: true, item: r })} data-testid={`edit-${collection}-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
            {canWrite && canDelete(r) && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDel(r)} data-testid={`delete-${collection}-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
          </>
        )} />
      <CrudDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={title} fields={fields} initial={dlg.item} basePath={path} onSaved={refetch} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={doDelete} />
      <ConfirmDialog open={bulk} onOpenChange={setBulk} onConfirm={doBulkDelete} title={`Hapus ${selected.size} data terpilih?`}
        description="Semua baris yang dicentang akan dihapus (soft delete) dan tercatat di audit trail. Tindakan ini tidak bisa dibatalkan dari UI." confirmText={`Hapus ${selected.size} data`} />
    </div>
  );
}
